import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { api, ApiError } from "../lib/api";
import type { GeneratedResume, VaultItem } from "../types";

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-300 focus:border-teal focus:outline-none";

type ResumeListItem = { id: string; target_company_name: string | null; created_at: string };

const POPULAR_CATEGORIES = ["Product Manager", "Software Engineer", "UX Designer", "Marketing", "Data Analyst", "Sales"];

const STEPS = [
  { n: 1, label: "Job & Company" },
  { n: 2, label: "Select Experience" },
  { n: 3, label: "Generate & Edit" },
  { n: 4, label: "Final Review" },
];

export default function AIResume() {
  const { user, subscription, credits, refreshSubscription } = useAuth();
  const navigate = useNavigate();

  const isPaid =
    subscription &&
    (subscription.plan === "pro_talent" || subscription.plan === "team_fit" || subscription.plan === "scale_up") &&
    (subscription.status === "active" || subscription.status === "trialing");
  const isOutOfCredits = !isPaid && (credits?.report_credits ?? 0) <= 0;

  const [step, setStep] = useState(1);
  const [jobMode, setJobMode] = useState<"url" | "manual">("url");
  const [targetCompanyName, setTargetCompanyName] = useState("");
  const [targetJobUrl, setTargetJobUrl] = useState("");
  const [targetJobText, setTargetJobText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedResume | null>(null);
  const [tab, setTab] = useState<"resume" | "cover" | "both">("resume");
  const [history, setHistory] = useState<ResumeListItem[]>([]);
  const [vault, setVault] = useState<VaultItem[]>([]);
  const [vaultLoading, setVaultLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ items: ResumeListItem[] }>("/api/resume")
      .then((res) => setHistory(res.items))
      .catch(() => undefined);
  }, [result]);

  const [shareCopied, setShareCopied] = useState(false);

  async function handleShareResume() {
    if (!result) return;
    const text = tab === "cover" ? result.coverLetter : JSON.stringify(result, null, 2);
    let success = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        success = true;
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch {
      success = false;
    }
    if (success) {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  }

  useEffect(() => {
    api
      .get<{ items: VaultItem[] }>("/api/persona/vault")
      .then((res) => setVault(res.items))
      .catch(() => undefined)
      .finally(() => setVaultLoading(false));
  }, []);

  function goToStep(n: number) {
    if (n === 2 && !targetCompanyName && !targetJobUrl && !targetJobText) {
      setError("Please enter at least one of target company name, job URL, or job description.");
      return;
    }
    setError(null);
    setStep(n);
  }

  async function generate() {
    if (isOutOfCredits) {
      setError("You have 0 report credits remaining. Please upgrade your plan.");
      return;
    }
    setGenerating(true);
    setError(null);
    setResult(null);
    setStep(3);
    try {
      const data = await api.post<GeneratedResume>("/api/resume/generate", {
        targetCompanyName: targetCompanyName || undefined,
        targetJobUrl: targetJobUrl || undefined,
        targetJobText: targetJobText || undefined,
      });
      setResult(data);
      setTab("resume");
      setStep(4);
      await refreshSubscription();
    } catch (err) {
      if (err instanceof ApiError && err.code === "OUT_OF_CREDITS") {
        setError("You have 0 report credits remaining. Please upgrade your plan to generate resumes.");
        await refreshSubscription();
      } else {
        setError(err instanceof Error ? err.message : "Failed to generate resume.");
      }
      setStep(2);
    } finally {
      setGenerating(false);
    }
  }

  function startOver() {
    setResult(null);
    setStep(1);
  }

  return (
    <AppLayout title="AI Resume">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Link to="/app" className="hover:text-navy">← Back to Dashboard</Link>
        </div>
        {!isPaid && (
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                isOutOfCredits ? "bg-rose-100 text-rose-700 border-rose-300 animate-pulse" : "bg-slate-50 text-slate-700 border-slate-200"
              }`}
            >
              💳 Report Credits: {credits?.report_credits ?? 0}
            </span>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white transition hover:bg-navy-light"
            >
              ⚡ Upgrade Plan
            </button>
          </div>
        )}
      </div>

      {isOutOfCredits && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-rose-900 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-500 text-white font-bold text-sm">⚡</span>
              <div>
                <p className="text-xs font-bold sm:text-sm">0 Report Credits Remaining</p>
                <p className="text-[11px] text-rose-700">You need report credits to generate tailored AI resumes. Upgrade your plan to get unlimited resumes.</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-1 rounded-lg bg-teal px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-teal-dark"
            >
              ⚡ Upgrade Plan →
            </button>
          </div>
        </div>
      )}

      {!result ? (
        <>
          <span className="inline-flex items-center rounded-full bg-teal/10 px-2.5 py-1 text-[10px] font-semibold text-teal-dark">
            AI-Powered
          </span>
          <h1 className="mt-2 text-xl font-bold text-navy sm:text-2xl">Create Your AI-Powered Resume</h1>
          <p className="mt-1 max-w-xl text-xs leading-relaxed text-slate-500">
            AI automatically adapts your Master Career Vault experiences for your target company and role.
          </p>

          {/* Stepper */}
          <div className="mt-6 flex items-center overflow-x-auto pb-1">
            {STEPS.map((s, i) => (
              <div key={s.n} className="flex flex-shrink-0 items-center">
                <button
                  onClick={() => (s.n < step ? goToStep(s.n) : undefined)}
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition ${
                    s.n === step
                      ? "bg-navy text-white"
                      : s.n < step
                      ? "bg-teal text-white"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {s.n < step ? "✓" : s.n}
                </button>
                <span className={`ml-2 whitespace-nowrap text-xs font-medium ${s.n <= step ? "text-navy" : "text-slate-400"}`}>
                  {s.label}
                </span>
                {i < STEPS.length - 1 && <span className="mx-3 h-px w-8 flex-shrink-0 bg-slate-200 sm:w-16" />}
              </div>
            ))}
          </div>

          {error && <p className="mt-3 text-xs text-coral">{error}</p>}

          {/* Step 1 */}
          {step === 1 && (
            <Card className="mt-4 border border-slate-100">
              <p className="text-sm font-semibold text-navy">1. Job & Company</p>
              <p className="mt-0.5 text-xs text-slate-400">Tell us the position and company you're applying to.</p>

              <div className="mt-3 flex gap-2 border-b border-slate-100">
                <TabButton active={jobMode === "url"} onClick={() => setJobMode("url")}>Enter Job URL</TabButton>
                <TabButton active={jobMode === "manual"} onClick={() => setJobMode("manual")}>Manual Input</TabButton>
              </div>

              <div className="mt-4">
                <Field label="Target Company Name">
                  <input className={inputClass} value={targetCompanyName} onChange={(e) => setTargetCompanyName(e.target.value)} placeholder="Google" />
                </Field>
                {jobMode === "url" ? (
                  <Field label="Job Posting URL">
                    <input className={inputClass} value={targetJobUrl} onChange={(e) => setTargetJobUrl(e.target.value)} placeholder="https://example.com/jobs/12345" />
                  </Field>
                ) : (
                  <Field label="Paste Job Description (Improves Accuracy)">
                    <textarea className={inputClass} rows={5} value={targetJobText} onChange={(e) => setTargetJobText(e.target.value)} placeholder="Paste job description text here..." />
                  </Field>
                )}
              </div>

              <p className="mb-2 mt-4 text-xs font-medium text-slate-500">Popular Job Categories</p>
              <div className="flex flex-wrap gap-1.5">
                {POPULAR_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setTargetJobText((prev) => (prev ? prev : `Role: ${cat}`))}
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-[11px] font-medium text-slate-500 transition hover:border-teal hover:text-teal-dark"
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <Button onClick={() => goToStep(2)}>Next: Select Experience →</Button>
              </div>
            </Card>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <Card className="mt-4 border border-slate-100">
              <p className="text-sm font-semibold text-navy">2. Select Experience</p>
              <p className="mt-0.5 text-xs text-slate-400">
                AI automatically selects relevant experiences and skills from your Career Vault for the target role.
              </p>

              <div className="mt-4 space-y-2">
                {vaultLoading && <p className="text-xs text-slate-400">Loading...</p>}
                {!vaultLoading && vault.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center">
                    <p className="text-xs text-slate-400">No experiences added to your Career Vault yet.</p>
                    <Link to="/app/persona" className="mt-2 inline-block text-xs font-semibold text-teal-dark">
                      Add experiences to Career Vault →
                    </Link>
                  </div>
                )}
                {vault.map((v) => (
                  <div key={v.id} className="flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-teal text-[10px] text-white">✓</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-navy">
                        {v.title} <span className="font-normal text-slate-400">· {v.organization || "—"}</span>
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {v.type} {v.start_date ? `· ${v.start_date} ~ ${v.is_current ? "Present" : v.end_date ?? ""}` : ""}
                      </p>
                    </div>
                    <span className="flex-shrink-0 rounded-full bg-teal/10 px-2 py-0.5 text-[10px] font-medium text-teal-dark">
                      Selected
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>← Previous</Button>
                <Button onClick={generate} loading={generating}>Next: Generate Resume →</Button>
              </div>
            </Card>
          )}

          {/* Step 3 (loading) */}
          {step === 3 && (
            <Card className="mt-4 flex min-h-[240px] flex-col items-center justify-center border border-slate-100 text-center">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-teal/20 border-t-teal" />
              <p className="mt-4 text-sm font-semibold text-navy">AI is writing your resume...</p>
              <p className="mt-1 text-xs text-slate-400">Adapting your Career Vault experiences for your target role.</p>
            </Card>
          )}
        </>
      ) : (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-navy">Your resume is ready 🎉</p>
              <p className="text-xs text-slate-400">4. Final Review</p>
            </div>
            <div className="flex gap-2">
              <button className="text-xs font-medium text-teal-dark" onClick={startOver}>
                Create New
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_280px]">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  <TabButton active={tab === "resume"} onClick={() => setTab("resume")}>Resume</TabButton>
                  <TabButton active={tab === "cover"} onClick={() => setTab("cover")}>Cover Letter</TabButton>
                  <TabButton active={tab === "both"} onClick={() => setTab("both")}>Both</TabButton>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.print()}
                  >
                    ⬇ Download
                  </Button>
                  <Button
                    size="sm"
                    variant={shareCopied ? "secondary" : "primary"}
                    onClick={handleShareResume}
                  >
                    {shareCopied ? "✓ Copied!" : "↗ Share"}
                  </Button>
                </div>
              </div>

              <Card className="mt-3 border border-slate-100">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-teal to-coral text-white">
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      user?.name?.[0] ?? "?"
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-navy">{user?.name}</p>
                    <p className="text-xs text-slate-400">{result.experience[0]?.title || "—"}</p>
                  </div>
                </div>

                {(tab === "resume" || tab === "both") && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-slate-400">SUMMARY</p>
                      <p className="mt-1 text-sm leading-relaxed text-navy">{result.summary}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-400">EXPERIENCE</p>
                      <div className="mt-1 space-y-3">
                        {result.experience.map((exp, i) => (
                          <div key={i}>
                            <p className="text-sm font-semibold text-navy">
                              {exp.title} · {exp.organization}
                            </p>
                            <p className="text-[11px] text-slate-400">{exp.period}</p>
                            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-slate-600">
                              {exp.bullets.map((b, j) => (
                                <li key={j}>{b}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                    {result.education.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-400">EDUCATION</p>
                        <div className="mt-1 space-y-1">
                          {result.education.map((ed, i) => (
                            <p key={i} className="text-xs text-slate-600">
                              {ed.school} · {ed.degree} ({ed.period})
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-slate-400">SKILLS</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {result.skills.map((s) => (
                          <span key={s} className="rounded-full bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {tab === "both" && <div className="my-5 border-t border-dashed border-slate-200" />}

                {(tab === "cover" || tab === "both") && (
                  <div>
                    {tab === "both" && <p className="mb-1 text-xs font-semibold text-slate-400">COVER LETTER</p>}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-navy">{result.coverLetter}</p>
                  </div>
                )}
              </Card>
            </div>

            {/* Side info */}
            <div className="space-y-4">
              <Card className="border border-slate-100 bg-gradient-to-br from-navy to-navy-light text-white">
                <p className="text-sm font-bold">✨ Your resume is ready!</p>
                <p className="mt-1 text-xs leading-relaxed text-white/70">
                  This resume has been optimized for your target company and role.
                </p>
                <Button variant="secondary" size="sm" className="mt-3" fullWidth onClick={() => window.print()}>
                  Download PDF
                </Button>
              </Card>
              {history.length > 0 && (
                <Card className="border border-slate-100">
                  <p className="text-xs font-semibold text-navy">Recent Resumes</p>
                  <div className="mt-2 space-y-2">
                    {history.slice(0, 5).map((h) => (
                      <div key={h.id} className="text-xs text-slate-500">
                        {h.target_company_name || "Resume"}
                        <span className="ml-2 text-[10px] text-slate-400">
                          {new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function TabButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${active ? "bg-navy text-white" : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );
}
